import { useEffect, useState } from 'react';

const CACHE_PREFIX = 'img_cache_';
const CACHE_VERSION = 'v1';

// Preload and cache logo immediately on module load
const LOGO_CACHE_KEY = `${CACHE_PREFIX}${CACHE_VERSION}_atlas_logo`;

// Initialize logo cache synchronously from localStorage
const getCachedLogo = (): string | null => {
  try {
    return localStorage.getItem(LOGO_CACHE_KEY);
  } catch {
    return null;
  }
};

// Preload logo function - call this early in app lifecycle
export const preloadLogo = (logoSrc: string): void => {
  const cached = getCachedLogo();
  if (cached) return; // Already cached

  // Fetch and cache in background
  fetch(logoSrc)
    .then(response => response.blob())
    .then(blob => {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          localStorage.setItem(LOGO_CACHE_KEY, reader.result as string);
        } catch (e) {
          console.warn('Could not cache logo:', e);
        }
      };
      reader.readAsDataURL(blob);
    })
    .catch(console.error);
};

// Get cached logo URL or return original
export const getCachedLogoUrl = (originalSrc: string): string => {
  const cached = getCachedLogo();
  return cached || originalSrc;
};

// Hook specifically for logo - uses sync cached value for instant display
export const useLogoCache = (logoSrc: string) => {
  const [src, setSrc] = useState<string>(() => getCachedLogoUrl(logoSrc));
  const [isLoading, setIsLoading] = useState(() => !getCachedLogo());

  useEffect(() => {
    const cached = getCachedLogo();
    if (cached) {
      setSrc(cached);
      setIsLoading(false);
      return;
    }

    // Not cached, fetch and cache
    fetch(logoSrc)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          try {
            localStorage.setItem(LOGO_CACHE_KEY, base64);
            setSrc(base64);
          } catch {
            setSrc(logoSrc);
          }
          setIsLoading(false);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        setSrc(logoSrc);
        setIsLoading(false);
      });
  }, [logoSrc]);

  return { src, isLoading };
};

export const useImageCache = (src: string, cacheKey?: string) => {
  const [cachedSrc, setCachedSrc] = useState<string>(src);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const key = `${CACHE_PREFIX}${CACHE_VERSION}_${cacheKey || src}`;
    
    const loadImage = async () => {
      try {
        // Check localStorage first
        const cached = localStorage.getItem(key);
        if (cached) {
          setCachedSrc(cached);
          setIsLoading(false);
          return;
        }

        // Fetch and cache the image
        const response = await fetch(src);
        const blob = await response.blob();
        const reader = new FileReader();
        
        reader.onloadend = () => {
          const base64 = reader.result as string;
          try {
            localStorage.setItem(key, base64);
            setCachedSrc(base64);
          } catch (e) {
            // If localStorage is full, use original src
            console.warn('localStorage full, using original src');
            setCachedSrc(src);
          }
          setIsLoading(false);
        };
        
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error caching image:', error);
        setCachedSrc(src);
        setIsLoading(false);
      }
    };

    loadImage();
  }, [src, cacheKey]);

  return { src: cachedSrc, isLoading };
};
